import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { apiClient } from '@/services/api';
import styles from '@/widgets/LoginForm/styles.module.scss';
import { VERBATIM_INPUT_PROPS } from '@/constants';

interface FormData {
  email: string;
}

const ArrowIcon = () => (
  <svg className={styles.arrow} width="14" height="12" viewBox="0 0 14 12" fill="none" aria-hidden="true">
    <path d="M1 6h12m0 0-5-5m5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ForgotPasswordForm: React.FC = () => {
  const { t } = useTranslation('auth');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      const response = await apiClient.forgotPassword(data.email.trim().toLowerCase());
      if (response.data.success) {
        toast.success(t('resetLinkSent'));
        navigate('/login');
      } else {
        toast.error(response.data.message || t('sendError'));
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || t('sendErrorRetry');
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const onValidationError = () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (errors.email) toast.error(String(errors.email.message || t('emailInvalid')));
  };

  return (
    <article className={styles.loginCard}>
      <div className={styles.cardHead}>
        <span className={styles.eyebrow}>{t('accountRecovery')}</span>
        <h2 className={styles.cardTitle}>{t('resetPasswordAction')}</h2>
        <p className={styles.cardSub}>{t('enterEmailResetLink')}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onValidationError)}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor="forgot-email">{t('email')}</label>
          <input
            className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
            id="forgot-email"
            type="text"
            placeholder="you@example.com"
            disabled={isLoading}
            data-testid="forgot-email"
            autoComplete="email"
            {...VERBATIM_INPUT_PROPS}
            {...register('email', {
              required: t('emailRequired'),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: t('emailInvalid'),
              },
            })}
          />
        </div>

        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          type="submit"
          disabled={isLoading}
          data-testid="forgot-submit"
        >
          {isLoading ? t('sending') : t('sendResetLink')}
          {!isLoading && <ArrowIcon />}
        </button>

        <p style={{ fontSize: 12, color: 'var(--text)', marginTop: 12 }}>
          {t('resetLinkValidFor')}
        </p>
      </form>

      <div className={styles.cardFoot}>
        <span>{t('rememberPassword')}</span>
        <Link to="/login" className={styles.textBtn}>{t('signInAction')}</Link>
      </div>
    </article>
  );
};

export default ForgotPasswordForm;
